/* =====================================================================
   Alex & Lace — ABOUT HERO image parallax. GSAP + ScrollTrigger.

   WHY A DEDICATED MODULE (and not al-hero-motion): al-hero-motion.js is scoped to [data-al-hero] and
   is entangled with the homepage hero's CTA pin, shop-now dead-zone and footer-dock choreography —
   none of which belongs on About. al-about-hero deliberately never emits [data-al-hero], so this
   drift is owned here instead, exactly the way al-image-inset.js owns chapter 3's image drift. It
   invents NO new motion language: same scrubbed gsap.fromTo(y), same SCRUB, same matchMedia gate,
   same late-layout refresh as its two siblings. No pin, no position:fixed.

   MECHANIC — NOW THE HOMEPAGE TWEEN, VALUE FOR VALUE (CSS-parity pass 2026-07-26). al-hero-motion.js
   §1 is: `gsap.to(img, { y: () => CFG.imageDrift * vh(), ease:'none' })` on `{trigger: hero, start:
   'top top', end:'bottom top', scrub: CFG.scrub}` — a ONE-DIRECTIONAL lag DOWN of +0.115·vh across the
   hero's scroll-out, starting at rest (y=0) while the hero fills the viewport. This module reproduced
   the FEEL but not the values: a symmetric ±0.06·vh over `top bottom → bottom top`, i.e. ~40% less
   travel spread over ~1.8× the scroll distance (≈0.046 px/px vs the homepage's ≈0.077 px/px), which
   over the old 34vw band was barely legible as motion at all. Both halves of that are now the
   homepage's: same amplitude, same range, same scrub, same ease.

   Transform only → zero CLS. It composes with the curtain's 1.15→1 entrance zoom because that tween
   animates `scale` and this one animates `y`: different properties, no overwrite (the same
   composition al-hero-curtain.js relies on for the homepage hero).

   GATING: desktop ≥1024 + prefers-reduced-motion:no-preference via gsap.matchMedia(), which auto-
   reverts the transform when the query stops matching. Mobile (site rule: parallax off) and reduced-
   motion therefore render the static stacked layout, and JS-off never runs this at all.

   STORY-SCROLL — DRIFT IS NOW 0.34, NOT THE HOMEPAGE'S 0.115 (owner spec 2026-07-30). The hero became
   a ~180vh runway you scroll THROUGH, with four text beats revealing at increasing depths. The homepage
   coefficient was calibrated for a one-viewport hero whose whole job is to leave; spent over 180vh it
   would be a 103px creep across 1620px of scroll (0.064 px/px) — present in a measurement, absent as an
   experience. The spec asks for PRONOUNCED, and for the image to travel visibly SLOWER than the text so
   the two planes separate:

     travel  0.34·vh = 306px @900   → 2.96× this module's previous 103.5px
     rate    306 / 1620px          = 0.189 px/px → 2.4× the homepage's 0.078 px/px
     read    image moves at 0.81×  the text's scroll speed — the differential IS the depth

   0.34 IS A CEILING SET BY THE IMAGE, NOT A TASTE CALL. The source is 1440×2160 and the CDN never
   upscales past intrinsic, so the figure box cannot exceed 2160px tall at 1440 wide. Section height and
   bleed spend from that one budget, which is also why the section is 180vh rather than 200vh: the
   shorter runway leaves ~50% more room for travel. Full arithmetic in the section CSS. When the taller
   portrait source lands, DRIFT and the two bleed numbers move together — nothing else does.

   BLEED SAFETY: peak travel is DRIFT·vh downward, so the headroom that matters is at the TOP, and the
   bleed is now ASYMMETRIC for exactly that reason. The section CSS gives the backdrop top:-52vh /
   height:calc(100% + 56vh) inside the section's overflow:hidden clip — 52vh of headroom against 34vh of
   peak travel is 1.53×, al-hero's ratio. At full drift the top edge sits at -52vh + 34vh = -18vh, still
   162px above the clip @900; the bottom edge starts 36px below it and only travels further down. No edge
   can enter the frame at any scroll position, including during scrub overshoot on fast input. Verified
   across the full transit at every beat; see the About /.verify/ captures.
   ===================================================================== */

(() => {
  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  if (!gsap || !ScrollTrigger) return;

  const section = document.querySelector('[data-al-about-hero]');
  if (!section) return; // no-op off the About page

  // Only present when the merchant has set the hero image.
  const media = section.querySelector('[data-al-hero-img]');
  if (!media) return;

  gsap.registerPlugin(ScrollTrigger);

  // SCRUB is al-hero-motion.js's CFG.scrub verbatim (locked 2026-07-17). DRIFT is deliberately NOT the
  // homepage's any more — see the STORY-SCROLL note in the header block.
  const SCRUB = 0.9; // smoothing seconds — rides Lenis's lerped scroll
  const DRIFT = 0.34; // image lags DOWN +34%vh across the section's scroll-out (+306px @900)
  const vh = () => window.innerHeight;
  const mm = gsap.matchMedia();

  mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => {
    // Homepage §1, identically shaped: from rest (y=0, so nothing jumps at scroll 0) to +DRIFT·vh,
    // scrubbed across the section's own exit. `start:'top top'` — not 'top bottom' — is what keeps the
    // drift spent while the hero is ON SCREEN rather than half-wasted before it arrives.
    gsap.to(media, {
      y: () => DRIFT * vh(),
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        end: 'bottom top',
        scrub: SCRUB,
        invalidateOnRefresh: true,
      },
    });
  });

  // Late-layout refresh (CLAUDE guardrail): the Nohemi swap + Lenis's async height:auto both shift
  // layout AFTER this trigger is built; recompute bounds on fonts.ready / 'load' / al:lenis-ready.
  const refreshST = () => ScrollTrigger.refresh();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(refreshST);
  window.addEventListener('load', refreshST);
  window.addEventListener('al:lenis-ready', refreshST);
})();
